import { dayOfMonth, yearsBetween, type Day } from '../core/calendar.ts';
import { add, dec, div, isZero, mul, round2, type Decimal } from '../core/decimal.ts';
import type { Transaction } from '../types/domain.ts';
import { monthlyEquivalent } from './frequency.ts';

/**
 * Transaction.effectiveInterestRate(at:): the latest rate change on or before `date`
 * (the first listed wins a tie), else the base rate; null without a base rate.
 */
export function effectiveInterestRate(transaction: Transaction, date: Day): Decimal | null {
  const base = transaction.interestRate;
  if (base === null) return null;
  const latest = (transaction.interestRateChanges ?? [])
    .filter((change) => change.date <= date)
    .reduce<{ readonly date: Day; readonly newRate: Decimal } | null>(
      (best, change) => (best === null || change.date > best.date ? change : best),
      null,
    );
  return latest?.newRate ?? base;
}

/** Transaction.effectivePaymentDay(in:): paymentDayOfMonth (0 = last day) or the start date's day. */
export const effectivePaymentDay = (transaction: Transaction): number =>
  transaction.paymentDayOfMonth ?? dayOfMonth(transaction.startDate);

/** 1 + percent/100, the multiplier form every percentage takes in the Swift engine. */
export const percentMultiplier = (percent: Decimal): Decimal => add(dec(1), div(percent, dec(100)));

/**
 * AnnualIncrease.amount (R11): `amount` after one rise of `percent` on each anniversary of
 * `start` (whole years, §2.2), compounding yearly by repeated Decimal multiplication, rounded to
 * the penny once it has risen; unchanged before the first anniversary.
 */
export function annualIncreaseAmount(amount: Decimal, percent: Decimal, start: Day, date: Day): Decimal {
  if (isZero(percent)) return amount;
  const rises = yearsBetween(start, date);
  if (rises <= 0) return amount;
  const growth = percentMultiplier(percent);
  let multiplier = dec(1);
  for (let i = 0; i < rises; i++) multiplier = mul(multiplier, growth);
  return round2(mul(amount, multiplier));
}

/**
 * The item as it was on `date` (TIMELINE R24, decision 3): each dated change after `date` puts back
 * the fields it changed, the earliest such change winning; with no change after `date`, the item.
 */
export function fieldsOn(transaction: Transaction, date: Day): Transaction {
  const history = transaction.history;
  if (history === undefined || history.length === 0 || date >= history[history.length - 1]!.from) return transaction;
  let fields: Transaction = transaction;
  for (let i = history.length - 1; i >= 0 && date < history[i]!.from; i--) fields = { ...fields, ...history[i]!.was };
  return fields;
}

/** Transaction.amount(on:): the amount in force on `date` (R24) after its annual increases since the start (R11). */
export function amountOn(transaction: Transaction, date: Day): Decimal {
  const fields = fieldsOn(transaction, date);
  return annualIncreaseAmount(fields.amount, fields.annualIncreasePercent, transaction.startDate, date);
}

/** What a payment on `date` actually moves (R27): the amount a check-in recorded, else the amount in force. */
export const paidOn = (transaction: Transaction, date: Day): Decimal => transaction.actuals?.get(date) ?? amountOn(transaction, date);

/** Transaction.monthlyEquivalentAmount. */
export const monthlyEquivalentAmount = (transaction: Transaction): Decimal =>
  monthlyEquivalent(transaction.frequency, transaction.amount);
